import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Button, Card, CardActionArea, CircularProgress, Collapse, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, IconButton, InputBase,
  Stack, Switch, Tooltip, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';

import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';

import { db } from './firebase';
import {
  addDoc, collection, deleteDoc, doc, getDocs, orderBy, query,
  serverTimestamp, updateDoc
} from 'firebase/firestore';

/**
 * Dessert-only list manager (so Beverages can keep the original).
 * Now includes: Archive • Unarchive • Delete (with confirm).
 */
export default function RecipeManagerV2({
  schema,
  uiSchema,
  collectionName,
  defaultRecipeType,
  onBeforeSave,
  fields
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [showArchived, setShowArchived] = useState(false);

  // editor state
  const [open, setOpen] = useState(false);
  const [formTitle, setFormTitle] = useState('Add New Recipe');
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // delete confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* ------------------------ Fetch ------------------------ */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const q = query(collection(db, collectionName), orderBy('name'));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRows(list);
    } catch (ex) {
      setErr(ex?.message || 'Failed to load recipes.');
    } finally {
      setLoading(false);
    }
  }, [collectionName]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ---------------- Archive / Unarchive ------------------ */
  const archiveRecipe = useCallback(async (recipe, archived = true) => {
    try {
      await updateDoc(doc(db, collectionName, recipe.id), {
        archived,
        updated_at: new Date().toISOString()
      });
      setRows(prev => prev.map(r => (r.id === recipe.id ? { ...r, archived } : r)));
    } catch (ex) {
      console.error(ex);
      setErr(ex?.message || `Failed to ${archived ? 'archive' : 'unarchive'} recipe.`);
    }
  }, [collectionName]);

  /* ---------------- Delete ------------------ */
  const askDelete = (recipe) => {
    setDeleteTarget(recipe);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setErr('');
    try {
      await deleteDoc(doc(db, collectionName, deleteTarget.id));
      setRows(prev => prev.filter(r => r.id !== deleteTarget.id));
      setConfirmOpen(false);
      setDeleteTarget(null);
    } catch (ex) {
      console.error(ex);
      setErr(ex?.message || 'Failed to delete recipe.');
    } finally {
      setDeleting(false);
    }
  };

  /* ---------------- Editor open/save --------------------- */
  const openNew = () => {
    setFormTitle('Add New Recipe');
    setFormData({
      name: '',
      category: '',
      description: '',
      recipe_type: defaultRecipeType || undefined,
      archived: false
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setFormTitle('Edit Recipe');
    setFormData({ ...row });
    setOpen(true);
  };

  const closeEditor = () => {
    setOpen(false);
    setFormData({});
  };

  const handleSave = async ({ formData: model }) => {
    try {
      setSaving(true);
      setErr('');
      const prepped = onBeforeSave ? onBeforeSave({ ...model }) : { ...model };

      if (prepped.id) {
        const { id, ...rest } = prepped;
        await updateDoc(doc(db, collectionName, id), { ...rest });
        setRows(prev => prev.map(r => (r.id === id ? { id, ...rest } : r)));
      } else {
        const payload = {
          ...prepped,
          created_at: prepped.created_at ?? new Date().toISOString(),
          updated_at: new Date().toISOString(),
          recipe_type: prepped.recipe_type ?? defaultRecipeType ?? undefined
        };
        const ref = await addDoc(collection(db, collectionName), {
          ...payload,
          _created_server: serverTimestamp(),
          _updated_server: serverTimestamp()
        });
        setRows(prev => [...prev, { id: ref.id, ...payload }]);
      }

      setOpen(false);
    } catch (ex) {
      console.error(ex);
      setErr(ex?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- Filter & group ----------------------- */
  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter(r => (showArchived ? true : !r.archived))
      .filter(r => (term ? (r.name || '').toLowerCase().includes(term) : true));
  }, [rows, search, showArchived]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const r of visibleRows) {
      const key = r.category || '(Uncategorized)';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return Array.from(m.entries())
      .map(([cat, items]) => ({ category: cat, items }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [visibleRows]);

  useEffect(() => {
    const next = {};
    groups.forEach(g => { next[g.category] = true; });
    setExpanded(next);
  }, [groups.length]);

  /* ---------------- Render ------------------------------- */
  return (
    <Box sx={{ p: 2 }}>
      {/* Top bar */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            px: 1, py: 0.5, display: 'flex', alignItems: 'center',
            border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, width: 280
          }}
        >
          <SearchIcon fontSize="small" />
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Box>

        <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
          Add New Recipe
        </Button>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 'auto' }}>
          <Typography variant="body2">Show Archived</Typography>
          <Switch checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
        </Stack>
      </Stack>

      {loading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      )}

      {err && (
        <Typography color="error" sx={{ mb: 1 }}>
          {err}
        </Typography>
      )}

      {/* Groups */}
      <Stack spacing={2}>
        {groups.map(group => (
          <Card key={group.category} variant="outlined">
            <CardActionArea
              onClick={() => setExpanded(prev => ({ ...prev, [group.category]: !prev[group.category] }))}
              sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="h6">{group.category}</Typography>
              {expanded[group.category] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardActionArea>

            <Collapse in={!!expanded[group.category]}>
              <Divider />
              {group.items.map(row => (
                <Box key={row.id}>
                  <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25 }} spacing={2}>
                    <Typography sx={{ flex: 1 }}>{row.name}</Typography>

                    <Tooltip title="Edit">
                      <IconButton onClick={() => openEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {row.archived ? (
                      <>
                        <Tooltip title="Unarchive">
                          <IconButton onClick={() => archiveRecipe(row, false)}>
                            <UnarchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => askDelete(row)}>
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title="Archive">
                        <IconButton onClick={() => archiveRecipe(row, true)}>
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  <Divider />
                </Box>
              ))}

              {group.items.length === 0 && (
                <Typography sx={{ px: 2, py: 1.5 }} color="text.secondary">
                  No recipes
                </Typography>
              )}
            </Collapse>
          </Card>
        ))}

        {groups.length === 0 && !loading && (
          <Typography color="text.secondary">No recipes found.</Typography>
        )}
      </Stack>

      {/* Editor dialog */}
      <Dialog open={open} onClose={closeEditor} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {formTitle}
          <IconButton onClick={closeEditor}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Form
            schema={schema}
            uiSchema={uiSchema}
            formData={formData}
            onChange={({ formData }) => setFormData(formData)}
            onSubmit={handleSave}
            validator={validator}
            fields={fields}
            liveValidate={false}
            showErrorList="bottom"
            formContext={{ formData }}
          >
            <div />
          </Form>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Close</Button>
          <Button onClick={() => document.querySelector('form.rjsf')?.requestSubmit()} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Recipe</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to permanently delete{' '}
            <strong>{deleteTarget?.name || 'this recipe'}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={doDelete} disabled={deleting} variant="contained">
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
